import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBreakingNews, useNews, useAdverts } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function BreakingNewsTicker() {
  const { t } = useTranslation();
  const tickerRef = useRef<HTMLDivElement>(null);
  const { items: breakingItems } = useBreakingNews();
  const { items: articles } = useNews();
  const { items: adverts } = useAdverts();

  const translatedBreaking = useTranslatedList(breakingItems, ["headline"]);
  const translatedArticles = useTranslatedList(articles, ["headline"]);
  const translatedAdverts = useTranslatedList(adverts, ["title"]);

  const activeHeadlines = translatedBreaking
    .filter((b) => b.status === "active")
    .map((b) => b.headline)
    .filter(Boolean);

  const fallbackHeadlines = translatedArticles
    .filter((a) => a.status === "published")
    .slice(0, 6)
    .map((a) => a.headline);

  const advertHeadlines = translatedAdverts
    .filter((a) => a.status === "active" && a.marquee)
    .map((a) => `📣 ${a.title}`);

  const headlines = activeHeadlines.length > 0 ? activeHeadlines : fallbackHeadlines;
  const finalItems =
    headlines.length > 0 ? [...advertHeadlines, ...headlines] : advertHeadlines.length > 0 ? advertHeadlines : [t("home.welcome")];
  const label = activeHeadlines.length > 0 ? t("home.breaking") : advertHeadlines.length > 0 ? t("home.advertisement") : t("home.latestNewsLabel");

  // Restart the scroll animation whenever the ticker content changes.
  const tickerKey = finalItems.join("|");

  useEffect(() => {
    const ticker = tickerRef.current;
    if (!ticker) return;

    let animationId: number;
    let position = 0;
    const speed = 0.8;

    const animate = () => {
      position -= speed;
      const width = ticker.scrollWidth / 2;
      if (Math.abs(position) >= width) {
        position = 0;
      }
      ticker.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [tickerKey]);

  const allItems = [...finalItems, ...finalItems];

  return (
    <div className="bg-accent-500 overflow-hidden relative">
      <div className="flex items-center">
        <div className="bg-primary-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-wider flex-shrink-0 z-10 relative">
          <i className="ri-flashlight-fill mr-1"></i>
          {label}
        </div>
        <div className="overflow-hidden flex-1 relative">
          <div
            ref={tickerRef}
            className="flex items-center whitespace-nowrap will-change-transform"
          >
            {allItems.map((item, index) => (
              <span
                key={index}
                className="text-white text-sm font-medium px-8 inline-flex items-center gap-2"
              >
                <i className="ri-circle-fill text-[6px] text-white/60"></i>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}